import * as React from 'react';
import {TextField, Select, Button, Checkbox, Divider, CircularProgress, Switch, FormControlLabel} from '@mui/material';
import {Company, Option, Person, Subject} from '../../../../store/common-values/commonTypes';
import {Autocomplete} from '@mui/material';
import {Project} from '../../../../store/projects';
import 'date-fns';
import {Audit, Certification} from '../../../../store/projects/commonTypes';
import SaveIcon from '@mui/icons-material/Save';
import Stack from "@mui/material/Stack";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {styled} from "@mui/styles";

export interface ReportUploadFormProps {
    onSave: (reportFile: File, levelId: number, comments?: string) => void;
    onCancel: () => void;
    levels: Option[];
    newCertification: boolean;
    isLoading: boolean;
}

export interface ReportUploadFormState {
    levels: Option[];
    levelId?: number;
    newCertification: boolean;
    comments: string;
    isLoading: boolean;
    reportFile: File | null;
}

const VisuallyHiddenInput = styled('input')({
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default class ReportUploadForm extends React.Component<ReportUploadFormProps, ReportUploadFormState> {
    constructor(props: ReportUploadFormProps) {
        super(props);

        this.state = {
            levels: props.levels,
            newCertification: props.newCertification,
            isLoading: props.isLoading,
            comments: '',
            reportFile: null
        };
    }

    componentDidUpdate(prevProps: ReportUploadFormProps) {
        if (this.props.levels?.length !== prevProps.levels?.length) {
            this.setState({levels: this.props.levels || []});
        }
        if (this.props.newCertification !== prevProps.newCertification) {
            this.setState({newCertification: this.props.newCertification});
        }
        if (this.props.isLoading !== prevProps.isLoading) {
            this.setState({isLoading: this.props.isLoading});
        }
    }

    private isValid(): boolean {
        if (this.state.newCertification && !this.state.levelId)
            return false;

        if (this.state.reportFile == null)
            return false;

        return true;
    }

    private setField(event: any, setValue: (s: ReportUploadFormState, value: any) => void) {
        const {target} = event;
        this.updateState(s => setValue(s, target?.value));
    }

    private updateState(action: (s: ReportUploadFormState) => void): void {
        this.setState(s => {
            action(s);
            return s;
        });
    }

    public render() {
        return (
            <React.Fragment>
                {this.state.newCertification && (
                    <div className="row mb-3">
                        <div className="col-sm-12">
                            <span>Serifiseringsnivå</span><span className="required">*</span>
                        </div>
                        <div className="col-sm-12">
                            <Autocomplete
                                options={this.state.levels.filter(_ => _.id != 283210005)}
                                disabled={this.state.isLoading}
                                className="form-control-input"
                                getOptionLabel={(option: Option) => option.label}
                                onChange={(e, option) => this.updateState(s => s.levelId = option?.id)}
                                renderInput={(params) => <TextField {...params} placeholder="Valg serifiseringsnivå"/>}
                            />
                        </div>
                    </div>
                )}
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Annen relevant informasjon</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg annen relevant informasjon"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            multiline={true}
                            minRows={3}
                            value={this.state.comments}
                            onChange={(event: any) => this.setField(event, (s, v) => s.comments = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <Stack direction="row" className="align-items-center" gap={3}>
                            <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                disabled={this.state.isLoading}
                                tabIndex={-1}
                                startIcon={<CloudUploadIcon />}
                            >
                                Last opp rapport
                                <VisuallyHiddenInput
                                    type="file"
                                    disabled={this.state.isLoading}
                                    onChange={(e) => this.setState({reportFile: e.target == null || e.target.files == null || e.target.files.length == 0 ? null : e.target.files[0]})}/>
                            </Button>
                            {this.state.reportFile ? (<label>{this.state.reportFile?.name}</label>) : ""}
                        </Stack>
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <Stack direction="row" gap={3}>
                            <Button className="mr-3" variant="contained"
                                    disabled={this.state.isLoading || !this.isValid()} color="primary"
                                    onClick={() => this.props.onSave(this.state.reportFile as File, this.state.levelId as number, this.state.comments)}>
                                {this.state.isLoading ? (<CircularProgress className="mr-3" size={24}/>) : (
                                    <SaveIcon className="mr-3"/>)}
                                Send
                            </Button>
                            <Button variant="contained" disabled={this.state.isLoading}
                                    onClick={() => this.props.onCancel()}>Avbryt</Button>
                        </Stack>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}